/**
 * FundraisingHome — Fundraising Suite, donor management component I.
 *
 * Exec / board rollup for fundraising. Pure client-side aggregation of data
 * that already exists — no new backend: donations (raised MTD/YTD, fees, top
 * donors), the donor pipeline (open + weighted value by stage), and upcoming
 * donor events (RSVP + raised). cbo-scoped via authFetch on each client.
 */

import React, { useEffect, useState } from 'react';
import { Calendar, DollarSign, RefreshCw, Target, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTabbedChrome } from '@/components/weavein';
import { Donation, listDonations } from '@/services/api/donations';
import { DonorOpportunity, DonorPipeline, getDonorPipeline, listDonorOpportunities } from '@/services/api/donorPipeline';
import { DonorEvent, listDonorEvents } from '@/services/api/donorEvents';

function money(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtEventDate(s?: string | null): string {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface Data {
  donations: Donation[];
  pipeline: DonorPipeline | null;
  opps: DonorOpportunity[];
  events: DonorEvent[];
}

function Kpi({
  icon,
  label,
  value,
  sub,
  tint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tint: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
          <span className={tint}>{icon}</span> {label}
        </div>
        <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
        {sub ? <div className="mt-0.5 text-xs text-gray-400">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

export const FundraisingHome: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const [donations, pipeline, opps, events] = await Promise.all([
          listDonations({ limit: 500 }).catch(() => [] as Donation[]),
          getDonorPipeline().catch(() => null),
          listDonorOpportunities().catch(() => [] as DonorOpportunity[]),
          listDonorEvents(true).catch(() => [] as DonorEvent[]),
        ]);
        if (!cancelled) setData({ donations, pipeline, opps, events });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load fundraising data.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const now = new Date();
  const succeeded = (data?.donations || []).filter((d) => d.status === 'succeeded');
  const ytd = succeeded.filter((d) => new Date(d.created_at).getFullYear() === now.getFullYear());
  const mtd = ytd.filter((d) => new Date(d.created_at).getMonth() === now.getMonth());
  const raisedYtd = ytd.reduce((s, d) => s + (d.amount || 0), 0);
  const raisedMtd = mtd.reduce((s, d) => s + (d.amount || 0), 0);
  const feesYtd = ytd.reduce((s, d) => s + (d.platform_fee || 0), 0);

  // Pipeline value by stage (open only — exclude won/lost).
  const stages = data?.pipeline?.stages || [];
  const openStages = stages.filter((s) => !s.isWon && !s.isLost);
  const byStage = openStages.map((st) => {
    const opps = (data?.opps || []).filter((o) => o.stage === st.id);
    const value = opps.reduce((s, o) => s + (o.amount || 0), 0);
    return { stage: st, count: opps.length, value };
  });
  const pipelineOpen = byStage.reduce((s, b) => s + b.value, 0);
  const pipelineWeighted = (data?.opps || [])
    .filter((o) => openStages.some((s) => s.id === o.stage))
    .reduce((s, o) => s + (o.amount || 0) * ((o.probability || 0) / 100), 0);
  const maxStageValue = Math.max(1, ...byStage.map((b) => b.value));

  // Top donors (succeeded gifts, grouped).
  const donorMap = new Map<string, { name: string; total: number }>();
  for (const d of succeeded) {
    const key = d.donor_member_id != null ? `m:${d.donor_member_id}` : `n:${(d.donor_name || 'Anonymous').toLowerCase()}`;
    const cur = donorMap.get(key) || { name: d.donor_name || 'Anonymous', total: 0 };
    cur.total += d.amount || 0;
    donorMap.set(key, cur);
  }
  const topDonors = [...donorMap.values()].sort((a, b) => b.total - a.total).slice(0, 5);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!tabbed && <TrendingUp size={26} className="text-blue-600" />}
          <div>
            {!tabbed && <h1 className="text-xl font-semibold text-gray-900">Fundraising</h1>}
            <p className="text-sm text-gray-500">Raised, pipeline, and upcoming events at a glance.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
          <RefreshCw size={15} className="mr-1" /> Refresh
        </Button>
      </header>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {data === null ? (
        <div className="py-16 text-center text-gray-400">Loading fundraising data…</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              icon={<DollarSign size={14} />}
              label="Raised YTD"
              value={money(raisedYtd)}
              sub={`${ytd.length} gift${ytd.length === 1 ? '' : 's'}`}
              tint="text-green-600"
            />
            <Kpi icon={<DollarSign size={14} />} label="Raised MTD" value={money(raisedMtd)} tint="text-green-600" />
            <Kpi
              icon={<Target size={14} />}
              label="Open pipeline"
              value={money(pipelineOpen)}
              sub={`${money(pipelineWeighted)} weighted`}
              tint="text-indigo-600"
            />
            <Kpi icon={<TrendingUp size={14} />} label="Platform fees YTD" value={money(feesYtd)} tint="text-gray-500" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Pipeline by stage */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target size={16} className="text-indigo-600" /> Pipeline by stage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {byStage.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-400">No open opportunities.</p>
                ) : (
                  byStage.map((b) => (
                    <div key={b.stage.id}>
                      <div className="mb-0.5 flex items-center justify-between text-xs text-gray-600">
                        <span>
                          {b.stage.name} <span className="text-gray-400">· {b.count}</span>
                        </span>
                        <span className="font-medium text-gray-900">{money(b.value)}</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded bg-gray-100">
                        <div
                          className="h-full rounded"
                          style={{
                            width: `${(b.value / maxStageValue) * 100}%`,
                            backgroundColor: b.stage.color || '#6366f1',
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Top donors */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users size={16} className="text-green-600" /> Top donors (YTD+)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topDonors.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-400">No gifts recorded yet.</p>
                ) : (
                  <div className="divide-y">
                    {topDonors.map((d, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-900">
                          <span className="mr-2 text-gray-400">{i + 1}.</span>
                          {d.name}
                        </span>
                        <span className="font-medium text-gray-900">{money(d.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar size={16} className="text-blue-600" /> Upcoming events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.events || []).length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-400">No upcoming donor events.</p>
              ) : (
                <div className="divide-y">
                  {data.events.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between py-2 text-sm">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900">{ev.title}</span>
                        <span className="ml-2 text-xs text-gray-400">{fmtEventDate(ev.start_time)}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span>
                          {ev.accepted}/{ev.invited} RSVP
                        </span>
                        {ev.raised > 0 ? <span className="font-medium text-green-700">{money(ev.raised)}</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default FundraisingHome;
